import { useEffect } from 'react';
import type { PersonAction, PersonView } from '@london-life/contracts';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { PersonCard } from '../people/person-card';
import { usePeople } from '../people/use-people';

// The People tab: everyone your character knows, closest first; requests waiting for an answer at the top;
// blocked people at the bottom so they can be unblocked. Meeting people happens out in the city (Who's here).
export function PeopleTab({
  placeId,
  onMessage,
  onChat,
}: {
  placeId: string | null;
  onMessage: (message: string) => void;
  onChat?: (lifeId: string) => void;
}) {
  const people = usePeople(true, placeId);
  useEffect(() => {
    if (people.message) {
      onMessage(people.message);
      people.clearMessage();
    }
  }, [people, onMessage]);
  const act = (lifeId: string, body: PersonAction) => people.act(lifeId, body);
  const list = people.reply?.people ?? [];
  const asking = list.filter((person) => person.pending && !person.pending.askedByMe && !person.blocked);
  const known = list.filter((person) => !asking.includes(person) && !person.blocked);
  const blocked = list.filter((person) => person.blocked);
  const section = (title: string, items: PersonView[]) =>
    items.length > 0 && (
      <section className="people-section">
        <h3>{title}</h3>
        <ul className="people-list">
          {items.map((person) => (
            <PersonCard key={person.lifeId} person={person} busy={people.busy} onAct={act} onChat={onChat} />
          ))}
        </ul>
      </section>
    );
  return (
    <div className="people-tab">
      {people.failure && <ErrorNotice error={people.failure} onRetry={() => void people.reload()} />}
      {people.reply && !list.length && (
        <p className="house-hint">
          You have not met anyone yet. Go somewhere in the city: anyone there appears under Who's here, and a hello is a
          start.
        </p>
      )}
      {section('Waiting for your answer', asking)}
      {section('People you know', known)}
      {section('Blocked', blocked)}
    </div>
  );
}
